import {
  add,
  endOfMonth,
  parseISO,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns";
import { MINUTES_PER_HOUR } from "src/utils/time/time.constants";
import { URL_DATE_DELIMITERS } from "./TimeWindow.constants";
import type { TimeWindow } from "./TimeWindow.types";
import { urlTimeWindow } from "./TimeWindow.urlSerialization";

export type UserTzContext = {
  serverTime: string | undefined;
  tz: number;
  fetchedAt: number;
  now?: number;
};

type CalendarPresetDef = {
  id: string;
  label: string;
  tooltip: string;
  getRange: (ctx: UserTzContext) => [Date, Date];
  /** When true, matcher requires actual end hour === expected end hour (e.g. MTD ends at "now"). */
  matchEndHours: boolean;
};

const stripTimezoneSuffix = (serverTime: string): string => {
  const plusIndex = serverTime.indexOf("+");
  return plusIndex === -1 ? serverTime : serverTime.substring(0, plusIndex);
};

/** Current instant in the user's chosen UTC offset (matches useFormatWithTz / TimeZoneSelector). */
export const getNowInUserTz = (ctx: UserTzContext): Date => {
  const now = ctx.now ?? Date.now();

  if (!ctx.serverTime) {
    // Degraded mode when /api/info is unavailable — browser clock, re-based from
    // UTC so the user offset is not stacked on top of the browser's own offset.
    const browserOffsetMinutes = new Date(now).getTimezoneOffset();
    return add(new Date(now), {
      minutes: ctx.tz * MINUTES_PER_HOUR + browserOffsetMinutes,
    });
  }

  const secondsDiff = (now - ctx.fetchedAt) / 1000;
  return add(parseISO(stripTimezoneSuffix(ctx.serverTime)), {
    hours: ctx.tz,
    seconds: secondsDiff,
  });
};

// date-fns startOfMonth/endOfMonth on getNowInUserTz() is equivalent to
// new Date(y, m, d, h, m, s) with Y/M/D extracted from the shifted instant.
export const getMonthToDateRange = (ctx: UserTzContext): [Date, Date] => {
  const now = getNowInUserTz(ctx);
  const start = startOfMonth(now);
  return [start, now];
};

export const getPreviousMonthRange = (ctx: UserTzContext): [Date, Date] => {
  const now = getNowInUserTz(ctx);
  const previousMonth = subMonths(now, 1);
  const start = startOfMonth(previousMonth);
  // Calendar last second (23:59:59). serializeDate keeps hour 23 and drops
  // minutes; rangesMatch compares hours, so this still round-trips. API uses
  // the same endOfMonth via resolveTimeWindowSelection — do not force 23:00.
  const end = endOfMonth(previousMonth);
  return [start, end];
};

export const getYesterdayRange = (ctx: UserTzContext): [Date, Date] => {
  const yesterday = subDays(getNowInUserTz(ctx), 1);
  const start = startOfDay(yesterday);
  // URL matcher path only. serializeDate(..., true) keeps hour, drops minutes
  // (`_23` → 23:00). End must be 23:00:00 so a stored legacy `~~` round-trips.
  // API closed bound is separate: resolveTimeWindowSelection("yesterday") uses
  // endOfDay (23:59:59). Do not copy that here — it is a different job.
  const end = startOfDay(yesterday);
  end.setHours(23, 0, 0, 0);
  return [start, end];
};

export const CALENDAR_PRESETS = [
  {
    id: "yesterday",
    label: "Yesterday",
    tooltip: "The previous day of data",
    getRange: getYesterdayRange,
    matchEndHours: false,
  },
  {
    id: "month-to-date",
    label: "Month to date",
    tooltip: "The current month of data until today",
    getRange: getMonthToDateRange,
    matchEndHours: true,
  },
  {
    id: "previous-month",
    label: "Previous month",
    tooltip: "The previous month of data",
    getRange: getPreviousMonthRange,
    matchEndHours: true,
  },
] satisfies readonly CalendarPresetDef[];

export type CalendarPresetId = (typeof CALENDAR_PRESETS)[number]["id"];

export const CALENDAR_PRESET_LABELS: Record<CalendarPresetId, string> =
  Object.fromEntries(CALENDAR_PRESETS.map((p) => [p.id, p.label])) as Record<
    CalendarPresetId,
    string
  >;

export const CALENDAR_PRESET_IDS: CalendarPresetId[] = CALENDAR_PRESETS.map(
  (p) => p.id
);

const CALENDAR_PRESET_BY_ID = new Map(CALENDAR_PRESETS.map((p) => [p.id, p]));

/** Preserves end hours so inclusive IS_BETWEEN_TIMES covers the full end day. */
export const serializeCalendarPresetRange = (
  _preset: CalendarPresetId,
  range: [Date, Date],
  _ctx: UserTzContext
): string => {
  const [start, end] = range;
  const startStr = urlTimeWindow.serializeDate(start, false);
  const endStr = urlTimeWindow.serializeDate(end, true);
  return `${startStr}${URL_DATE_DELIMITERS.RANGE_DELIMITER}${endStr}`;
};

export const resolveCalendarPreset = (
  preset: CalendarPresetId,
  ctx: UserTzContext
): string => {
  const config = CALENDAR_PRESET_BY_ID.get(preset)!;
  return serializeCalendarPresetRange(preset, config.getRange(ctx), ctx);
};

const sameCalendarDate = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const rangesMatch = (
  actual: [Date, Date],
  expected: [Date, Date],
  matchEndHours: boolean
): boolean => {
  const [actualStart, actualEnd] = actual;
  const [expectedStart, expectedEnd] = expected;

  if (!sameCalendarDate(actualStart, expectedStart)) {
    return false;
  }

  if (!sameCalendarDate(actualEnd, expectedEnd)) {
    return false;
  }

  if (!matchEndHours) {
    return true;
  }

  // URL serialize keeps hour but drops minutes.
  return actualEnd.getHours() === expectedEnd.getHours();
};

export const isCalendarPresetTimeWindow = (
  timeWindow: TimeWindow,
  ctx: UserTzContext,
  preset: CalendarPresetId
): boolean => {
  if (timeWindow === preset) {
    return true;
  }

  const actualRange = urlTimeWindow.deserializeDateRangeStr(timeWindow);
  if (!actualRange) {
    return false;
  }

  const config = CALENDAR_PRESET_BY_ID.get(preset)!;
  const expectedRange = config.getRange(ctx);
  return rangesMatch(actualRange, expectedRange, config.matchEndHours);
};

export const getCalendarPresetId = (
  timeWindow: TimeWindow,
  ctx: UserTzContext
): CalendarPresetId | undefined => {
  if ((CALENDAR_PRESET_IDS as string[]).includes(timeWindow)) {
    return timeWindow as CalendarPresetId;
  }

  const actualRange = urlTimeWindow.deserializeDateRangeStr(timeWindow);
  if (!actualRange) {
    return undefined;
  }

  for (const config of CALENDAR_PRESETS) {
    const expectedRange = config.getRange(ctx);
    if (rangesMatch(actualRange, expectedRange, config.matchEndHours)) {
      return config.id;
    }
  }

  return undefined;
};

export const getCalendarPresetLabel = (
  timeWindow: TimeWindow,
  ctx?: UserTzContext
): string | undefined => {
  if ((CALENDAR_PRESET_IDS as string[]).includes(timeWindow)) {
    return CALENDAR_PRESET_LABELS[timeWindow as CalendarPresetId];
  }
  if (!ctx) {
    return undefined;
  }

  const presetId = getCalendarPresetId(timeWindow, ctx);
  return presetId ? CALENDAR_PRESET_LABELS[presetId] : undefined;
};
